import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { getReactivated } from '../services/dashboardApi';
import Card from './Card';

// REACTIVATED v1
// REVERT-COLORS v1
// LOOK3 v1
// LOOK4 v1
// LOOK5 v1
// LOOK6 v1
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const tick = { fill: 'var(--color-text-secondary)', fontSize: 11 };
const dmy = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '');
const label = (ym) => `${MON[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`;

function ReactivatedModal({ month, onClose }) {
  const [q, setQ] = useState('');
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const list = month.customers.filter((c) => !q.trim() || `${c.name} ${c.city} ${c.refParty || ''}`.toLowerCase().includes(q.trim().toLowerCase()));
  const showRp = month.customers.some((c) => c.refParty); // REFPARTY-COL v1 (admin only; server sends it to admins)
  return (
    <div role="dialog" aria-modal="true" aria-label="Reactivated customers" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--color-surface)', color: 'var(--color-text)', borderRadius: 14, width: 'min(900px, 100%)', maxHeight: '92vh', overflow: 'auto', padding: 'var(--space-4)', boxShadow: '0 20px 60px rgba(0,0,0,.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg, 18px)' }}>
            {label(month.ym)} — {month.count} customers active again · {fmt(month.boxes)} box
          </h2>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer / city" style={{ width: '100%', marginBottom: 8 }} />
        <div className="table-scroll" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Customer</th>
                {showRp && <th style={{ textAlign: 'left' }}>Ref. Party</th>}
                <th style={{ textAlign: 'left' }}>Last order before gap</th>
                <th style={{ textAlign: 'left' }}>Came back on</th>
                <th style={{ textAlign: 'right' }}>Box</th>
                <th style={{ textAlign: 'left' }}>Follow-up</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={showRp ? 6 : 5} style={{ color: 'var(--color-text-muted)' }}>No customers.</td></tr>}
              {list.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}{c.city ? <span style={{ color: 'var(--color-text-muted)' }}> · {c.city}</span> : null}</td>
                  {showRp && <td>{c.refParty || '—'}</td>}
                  <td>{dmy(c.prevLast)}</td>
                  <td>{dmy(c.firstDispatch)}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(c.boxes)}</td>
                  <td>{c.followedUp ? <b style={{ color: 'var(--color-success)' }}>✓ {c.reason}</b> : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function ReactivatedCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    let alive = true;
    getReactivated().then((d) => { if (alive) setData(d); }).catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, []);
  const months = data ? data.months : [];
  const rows = months.map((m) => ({ label: label(m.ym), ym: m.ym, Reactivated: m.count, 'After follow-up': m.followed }));
  const tot = months.reduce((a, m) => a + m.count, 0);
  const totF = months.reduce((a, m) => a + m.followed, 0);
  const totBox = months.reduce((a, m) => a + m.boxes, 0);
  const pick = (idx) => { if (months[idx]) setOpen(months[idx]); };
  return (
    <>
      <Card title="Inactive → Active customers (12 months)" style={{ marginBottom: 0 }}>
        {err && <p style={{ color: 'var(--color-danger)', margin: 0 }}>Could not load.</p>}
        {!data && !err && <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>Loading…</p>}
        {data && (
          <>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 6, fontSize: 'var(--font-size-sm)' }}>
              <span><b>{fmt(tot)}</b> customers came back</span>
              <span><b style={{ color: 'var(--color-success)' }}>{fmt(totF)}</b> after follow-up (reason noted)</span>
              <span><b>{fmt(totBox)}</b> box dispatched</span>
            </div>
            <div style={{ width: '100%', height: 260, cursor: 'pointer' }}>
              <ResponsiveContainer>
                <BarChart data={rows} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}
                  onClick={(st) => { if (st && st.activeTooltipIndex != null) pick(Number(st.activeTooltipIndex)); }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                  <XAxis dataKey="label" tick={tick} tickLine={false} axisLine={{ stroke: 'var(--color-border)' }} interval="preserveStartEnd" />
                  <YAxis tick={tick} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Reactivated" fill="#0E6F8A" radius={[4, 4, 0, 0]} maxBarSize={26} />
                  <Bar dataKey="After follow-up" fill="#237246" radius={[4, 4, 0, 0]} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              Active again = dispatched this month after no dispatch in the 3 months before. Click a month for the customer list.
            </div>
          </>
        )}
      </Card>
      {open && <ReactivatedModal month={open} onClose={() => setOpen(null)} />}
    </>
  );
}
